import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTPageURLV2ToSDKPageURLV2 } from '@wix/sdk-runtime/transformations/page-url-v2';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient } from '@wix/sdk-types';
import {
  CheckoutTemplate,
  CheckoutTemplateCreatedEnvelope,
  CheckoutTemplateDeletedEnvelope,
  CheckoutTemplateNonNullableFields,
  CheckoutTemplateUpdatedEnvelope,
  CheckoutTemplateUsedEnvelope,
  CheckoutTemplatesQueryBuilder,
  CreateCheckoutFromTemplateResponse,
  CreateCheckoutFromTemplateResponseNonNullableFields,
  RawHttpResponse,
  RawHttpResponseNonNullableFields,
  UpdateCheckoutTemplate,
  createAndRedirectToCheckout as universalCreateAndRedirectToCheckout,
  createCheckoutFromTemplate as universalCreateCheckoutFromTemplate,
  createCheckoutTemplate as universalCreateCheckoutTemplate,
  deleteCheckoutTemplate as universalDeleteCheckoutTemplate,
  getCheckoutTemplate as universalGetCheckoutTemplate,
  queryCheckoutTemplates as universalQueryCheckoutTemplates,
  updateCheckoutTemplate as universalUpdateCheckoutTemplate,
} from './ecom-v1-checkout-template-checkout-templates.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function createCheckoutTemplate(
  httpClient: HttpClient
): CreateCheckoutTemplateSignature {
  return (checkoutTemplate: CheckoutTemplate) =>
    universalCreateCheckoutTemplate(
      checkoutTemplate,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateCheckoutTemplateSignature {
  /**
   * Creates a checkout template.
   *
   * A checkout template is used to create a new checkout that will include predefined information. For example, a single link with
   * a `checkoutTemplateId` can be shared with customers and each time the link is clicked, a new checkout page will be created
   * for that customer with certain checkout information already populated.
   *
   * The customizable features include the option to allow or to lock coupon codes or gift cards. For example, if a store owner is
   * using the checkout template to offer a flash sale to their social media followers, they may want to lock the option to apply an
   * additional coupon on top of the sale being offered. If so, they can set `customization.lockedCoupon` to `true`.
   *
   * A checkout can be created with a checkout template by calling `createCheckoutFromTemplate()`. The site may add further customizations to the new checkout and then redirect the customer using the new checkout's `checkoutUrl`.
   * @param - Checkout template to create.
   * @returns Created checkout template.
   */
  (checkoutTemplate: CheckoutTemplate): Promise<
    CheckoutTemplate & CheckoutTemplateNonNullableFields
  >;
}

export function getCheckoutTemplate(
  httpClient: HttpClient
): GetCheckoutTemplateSignature {
  return (checkoutTemplateId: string) =>
    universalGetCheckoutTemplate(
      checkoutTemplateId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetCheckoutTemplateSignature {
  /**
   * Retrieves a checkout template.
   * @param - ID of the checkout template to retrieve.
   * @returns Retrieved checkout template.
   */
  (checkoutTemplateId: string): Promise<
    CheckoutTemplate & CheckoutTemplateNonNullableFields
  >;
}

export function updateCheckoutTemplate(
  httpClient: HttpClient
): UpdateCheckoutTemplateSignature {
  return (_id: string | null, checkoutTemplate: UpdateCheckoutTemplate) =>
    universalUpdateCheckoutTemplate(
      _id,
      checkoutTemplate,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateCheckoutTemplateSignature {
  /**
   * Updates a checkout template.
   *
   * If the info in a checkout template is updated, only new checkouts created from this template will include the updated items. Checkouts previously
   * created from this template before the update will not be affected.
   * @param - Checkout template ID.
   * @param - Checkout template info to update.
   * @returns Updated checkout template.
   */
  (_id: string | null, checkoutTemplate: UpdateCheckoutTemplate): Promise<
    CheckoutTemplate & CheckoutTemplateNonNullableFields
  >;
}

export function deleteCheckoutTemplate(
  httpClient: HttpClient
): DeleteCheckoutTemplateSignature {
  return (checkoutTemplateId: string) =>
    universalDeleteCheckoutTemplate(
      checkoutTemplateId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteCheckoutTemplateSignature {
  /**
   * Deletes a checkout template.
   *
   * If a checkout template is deleted and a customer attempts to create a checkout with that `checkoutTemplateId` then
   * the customer will be redirected to the domain site.
   * @param - ID of the checkout template to delete.
   */
  (checkoutTemplateId: string): Promise<void>;
}

export function queryCheckoutTemplates(
  httpClient: HttpClient
): QueryCheckoutTemplatesSignature {
  return () =>
    universalQueryCheckoutTemplates(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryCheckoutTemplatesSignature {
  /**
   * Creates a query to retrieve a list of checkout templates.
   *
   * The `queryCheckoutTemplates()` function builds a query to retrieve a list of checkout templates and returns a `CheckoutTemplatesQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
   *
   * You can refine the query by chaining `CheckoutTemplatesQueryBuilder` functions onto the query. `CheckoutTemplatesQueryBuilder` functions enable you to sort, filter, and control the results that `queryCheckoutTemplates()` returns.
   *
   * `queryCheckoutTemplates()` runs with the following `CheckoutTemplatesQueryBuilder` default that you can override:
   * + `ascending("_id")`
   *
   * The functions that are chained to `queryCheckoutTemplates()` are applied in the order they are called. For example, if you apply `ascending("status")` and then `ascending("_id")`, the results are sorted first by the `"status"`, and then, if there are multiple results with the same `"status"`, the items are sorted by `"_id"`.
   *
   * The following `CheckoutTemplatesQueryBuilder` functions are supported for the `queryCheckoutTemplates()` function. For a full description of the checkout template object, see the object returned for the `items` property in `CheckoutTemplatesQueryResult`.
   */
  (): CheckoutTemplatesQueryBuilder;
}

export function createCheckoutFromTemplate(
  httpClient: HttpClient
): CreateCheckoutFromTemplateSignature {
  return (checkoutTemplateId: string, siteId: string) =>
    universalCreateCheckoutFromTemplate(
      checkoutTemplateId,
      siteId,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateCheckoutFromTemplateSignature {
  /**
   * Creates a new checkout based on the checkout template.
   *
   * Before using this function, you must have a checkout template available. Create a checkout template with `createCheckoutTemplate()`.
   *
   * The customer can be directed to the new checkout using the checkout's `checkoutUrl`.
   * @param - ID of the checkout template to use to create a checkout from.
   * @param - ID of the site associated with the checkout template.
   */
  (checkoutTemplateId: string, siteId: string): Promise<
    CreateCheckoutFromTemplateResponse &
      CreateCheckoutFromTemplateResponseNonNullableFields
  >;
}

export function createAndRedirectToCheckout(
  httpClient: HttpClient
): CreateAndRedirectToCheckoutSignature {
  return (checkoutTemplateId: string, siteId: string) =>
    universalCreateAndRedirectToCheckout(
      checkoutTemplateId,
      siteId,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateAndRedirectToCheckoutSignature {
  /**
   * Creates a new checkout based on the checkout template and redirects to the new checkout page.
   *
   * Before using this function, you must have a checkout template available. Create a checkout template with `createCheckoutTemplate()`.
   *
   * To build a URL that uses this function, follow this format:
   * `https://www.wixapis.com/ecom/v1/checkout-templates/{checkoutTemplateId}/create-and-redirect-to-checkout?siteId={siteId}`
   *
   * To create a checkout but not automatically redirect to the checkout page, use `createCheckoutFromTemplate()`.
   * @param - ID of the checkout template to use to create a checkout.
   * @param - ID of the site associated with the checkout template.
   */
  (checkoutTemplateId: string, siteId: string): Promise<
    RawHttpResponse & RawHttpResponseNonNullableFields
  >;
}

export const onCheckoutTemplateCreated = EventDefinition(
  'wix.ecom.v1.checkout_template_created',
  true,
  (event: CheckoutTemplateCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.lineItems.catalogOverrideFields.image' },
            { path: 'entity.customLineItems.media' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.lineItems.catalogOverrideFields.physicalProperties.weight',
            },
            { path: 'entity.customLineItems.physicalProperties.weight' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'entity.customLineItems.url' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            {
              path: 'entity.customLineItems.subscriptionOptionInfo.subscriptionSettings.startDate',
            },
            { path: 'entity.customLineItems.serviceProperties.scheduledDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<CheckoutTemplateCreatedEnvelope>();
export const onCheckoutTemplateDeleted = EventDefinition(
  'wix.ecom.v1.checkout_template_deleted',
  true,
  (event: CheckoutTemplateDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'undefined.lineItems.catalogOverrideFields.image' },
            { path: 'undefined.customLineItems.media' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'undefined.lineItems.catalogOverrideFields.physicalProperties.weight',
            },
            { path: 'undefined.customLineItems.physicalProperties.weight' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'undefined.customLineItems.url' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            {
              path: 'undefined.customLineItems.subscriptionOptionInfo.subscriptionSettings.startDate',
            },
            {
              path: 'undefined.customLineItems.serviceProperties.scheduledDate',
            },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<CheckoutTemplateDeletedEnvelope>();
export const onCheckoutTemplateUpdated = EventDefinition(
  'wix.ecom.v1.checkout_template_updated',
  true,
  (event: CheckoutTemplateUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'entity.lineItems.catalogOverrideFields.image' },
            { path: 'entity.customLineItems.media' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'entity.lineItems.catalogOverrideFields.physicalProperties.weight',
            },
            { path: 'entity.customLineItems.physicalProperties.weight' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'entity.customLineItems.url' }],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            {
              path: 'entity.customLineItems.subscriptionOptionInfo.subscriptionSettings.startDate',
            },
            { path: 'entity.customLineItems.serviceProperties.scheduledDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<CheckoutTemplateUpdatedEnvelope>();
export const onCheckoutTemplateUsed = EventDefinition(
  'wix.ecom.v1.checkout_template_used',
  true,
  (event: CheckoutTemplateUsedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            {
              path: 'data.checkoutTemplate.lineItems.catalogOverrideFields.image',
            },
            { path: 'data.checkoutTemplate.customLineItems.media' },
            { path: 'data.checkout.lineItems.media' },
            { path: 'data.checkout.lineItems.catalogOverrideFields.image' },
          ],
        },
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            {
              path: 'data.checkoutTemplate.lineItems.catalogOverrideFields.physicalProperties.weight',
            },
            {
              path: 'data.checkoutTemplate.customLineItems.physicalProperties.weight',
            },
            { path: 'data.checkout.lineItems.physicalProperties.weight' },
            {
              path: 'data.checkout.lineItems.catalogOverrideFields.physicalProperties.weight',
            },
            {
              path: 'data.checkout.appliedDiscounts.merchantDiscount.percentage',
            },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'data.checkoutTemplate.customLineItems.url' },
            { path: 'data.checkout.lineItems.url' },
          ],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            {
              path: 'data.checkoutTemplate.customLineItems.subscriptionOptionInfo.subscriptionSettings.startDate',
            },
            {
              path: 'data.checkoutTemplate.customLineItems.serviceProperties.scheduledDate',
            },
            { path: 'data.checkout.createdDate' },
            { path: 'data.checkout.updatedDate' },
            {
              path: 'data.checkout.lineItems.subscriptionOptionInfo.subscriptionSettings.startDate',
            },
            { path: 'data.checkout.lineItems.serviceProperties.scheduledDate' },
            {
              path: 'data.checkout.shippingInfo.selectedCarrierServiceOption.logistics.deliveryTimeSlot.from',
            },
            {
              path: 'data.checkout.shippingInfo.selectedCarrierServiceOption.logistics.deliveryTimeSlot.to',
            },
            {
              path: 'data.checkout.shippingInfo.carrierServiceOptions.shippingOptions.logistics.deliveryTimeSlot.from',
            },
            {
              path: 'data.checkout.shippingInfo.carrierServiceOptions.shippingOptions.logistics.deliveryTimeSlot.to',
            },
            {
              path: 'data.checkout.membershipOptions.eligibleMemberships.expirationDate',
            },
            {
              path: 'data.checkout.membershipOptions.invalidMemberships.membership.expirationDate',
            },
            {
              path: 'data.checkout.subscriptionCharges.charges.cycleBillingDate',
            },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'data.checkout.billingInfo.address' },
            { path: 'data.checkout.shippingInfo.shippingDestination.address' },
            {
              path: 'data.checkout.shippingInfo.selectedCarrierServiceOption.logistics.pickupDetails.address',
            },
            {
              path: 'data.checkout.shippingInfo.carrierServiceOptions.shippingOptions.logistics.pickupDetails.address',
            },
          ],
        },
      ])
    )
)<CheckoutTemplateUsedEnvelope>();

export {
  ActionEvent,
  AdditionalFee,
  AdditionalFeeSource,
  Address,
  AddressLocation,
  AddressWithContact,
  AggregatedTaxBreakdown,
  ApplicableLineItems,
  ApplicationError,
  AppliedDiscount,
  AppliedDiscountDiscountSourceOneOf,
  AutoTaxFallbackCalculationDetails,
  BaseEventMetadata,
  BuyerInfo,
  BuyerInfoIdOneOf,
  CalculationErrors,
  CalculationErrorsShippingCalculationErrorOneOf,
  Carrier,
  CarrierError,
  CarrierErrors,
  CarrierServiceOption,
  CatalogOverrideFields,
  CatalogReference,
  ChannelType,
  Charge,
  ChargeType,
  Checkout,
  CheckoutCustomization,
  CheckoutTemplate,
  CheckoutTemplateCreatedEnvelope,
  CheckoutTemplateDeletedEnvelope,
  CheckoutTemplateNonNullableFields,
  CheckoutTemplateUpdatedEnvelope,
  CheckoutTemplateUsed,
  CheckoutTemplateUsedEnvelope,
  CheckoutTemplatesQueryBuilder,
  CheckoutTemplatesQueryResult,
  Color,
  ConversionInfo,
  Coupon,
  CreateAndRedirectToCheckoutRequest,
  CreateCheckoutFromTemplateRequest,
  CreateCheckoutFromTemplateResponse,
  CreateCheckoutFromTemplateResponseNonNullableFields,
  CreateCheckoutTemplateRequest,
  CreateCheckoutTemplateResponse,
  CreateCheckoutTemplateResponseNonNullableFields,
  CreatedBy,
  CreatedByIdOneOf,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  CustomContentReference,
  CustomField,
  CustomLineItem,
  CustomSettings,
  DeleteCheckoutTemplateRequest,
  DeleteCheckoutTemplateResponse,
  DeliveryAllocation,
  DeliveryLogistics,
  DeliveryTimeSlot,
  Description,
  DescriptionLine,
  DescriptionLineDescriptionLineValueOneOf,
  DescriptionLineName,
  DescriptionLineType,
  DescriptionLineValueOneOf,
  Details,
  DetailsKindOneOf,
  DiscountRule,
  DiscountRuleName,
  DiscountType,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  ExternalReference,
  FallbackReason,
  FieldViolation,
  FileType,
  FocalPoint,
  FreeTrialPeriod,
  FullAddressContactDetails,
  GetCheckoutTemplateRequest,
  GetCheckoutTemplateResponse,
  GetCheckoutTemplateResponseNonNullableFields,
  GiftCard,
  Group,
  HeadersEntry,
  IdentificationData,
  IdentificationDataIdOneOf,
  InvalidMembership,
  ItemAvailabilityInfo,
  ItemAvailabilityStatus,
  ItemTaxFullDetails,
  ItemType,
  ItemTypeItemType,
  ItemTypeItemTypeDataOneOf,
  JurisdictionType,
  LineItem,
  LineItemDiscount,
  ManualCalculationReason,
  Membership,
  MembershipName,
  MembershipOptions,
  MembershipPaymentCredits,
  MerchantDiscount,
  MessageEnvelope,
  MultiCurrencyPrice,
  NameInLineItem,
  NameInOther,
  Other,
  OtherCharge,
  PaymentOption,
  PaymentOptionType,
  PhysicalProperties,
  PickupDetails,
  PickupMethod,
  PlainTextValue,
  Policy,
  PriceDescription,
  PriceSummary,
  ProductName,
  QueryCheckoutTemplatesRequest,
  QueryCheckoutTemplatesResponse,
  QueryCheckoutTemplatesResponseNonNullableFields,
  RateType,
  RawHttpResponse,
  RawHttpResponseNonNullableFields,
  Region,
  RestoreInfo,
  RuleType,
  Scope,
  SecuredMedia,
  SelectedCarrierServiceOption,
  SelectedCarrierServiceOptionOtherCharge,
  SelectedCarrierServiceOptionPrices,
  SelectedMembership,
  SelectedMemberships,
  ServiceProperties,
  Severity,
  ShippingInfo,
  ShippingOption,
  ShippingPrice,
  ShippingRegion,
  SortOrder,
  Sorting,
  Status,
  StreetAddress,
  SubscriptionCharges,
  SubscriptionFrequency,
  SubscriptionOptionInfo,
  SubscriptionSettings,
  SuggestedFix,
  SystemError,
  Target,
  TargetLineItem,
  TargetTargetTypeOneOf,
  TaxBreakdown,
  TaxCalculationDetails,
  TaxCalculationDetailsCalculationDetailsOneOf,
  TaxRateBreakdown,
  TaxSummary,
  TaxableAddress,
  TaxableAddressTaxableAddressDataOneOf,
  TaxableAddressType,
  Title,
  UpdateCheckoutTemplate,
  UpdateCheckoutTemplateRequest,
  UpdateCheckoutTemplateResponse,
  UpdateCheckoutTemplateResponseNonNullableFields,
  V1LineItem,
  ValidationError,
  VatId,
  VatType,
  Violation,
  WebClientCustomization,
  WebhookIdentityType,
  WeightUnit,
} from './ecom-v1-checkout-template-checkout-templates.universal.js';
